import React from 'react';
import {
  BookOpen,
  Users,
  Award,
  TrendingUp,
  Lightbulb,
  Settings,
  Shield,
  Star,
  Briefcase,
  Globe
} from 'lucide-react';
import '../Css_files/civil.css';
import Navbar from './Navbar';
import Footer from './Footer';

const OnlineLearning = () => {
  const programs = [
    {
      title: 'Full-Stack Web Development',
      duration: '6 Months',
      description: 'Learn front-end and back-end development with hands-on projects.',
      features: ['HTML, CSS, JavaScript, React', 'Node.js & Express', 'Database Integration', 'Capstone Project']
    },
    {
      title: 'Data Science & AI',
      duration: '8 Months',
      description: 'Master data analytics, machine learning, and AI tools for real-world applications.',
      features: ['Python & R Programming', 'Machine Learning Models', 'Deep Learning & AI Tools', 'Industry Projects']
    },
    {
      title: 'Digital Marketing Mastery',
      duration: '4 Months',
      description: 'Build expertise in SEO, social media, and performance marketing.',
      features: ['SEO & SEM', 'Social Media Campaigns', 'Google Ads & Analytics', 'Live Projects']
    }
  ];

  const faculty = [
    {
      name: 'Dr. Ananya Rao',
      position: 'Lead Instructor',
      specialization: 'Full-Stack Development',
      experience: '12+ years',
      image: 'https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Mr. Arjun Mehta',
      position: 'Senior Instructor',
      specialization: 'Data Science & AI',
      experience: '10+ years',
      image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Ms. Kavya Sharma',
      position: 'Digital Marketing Expert',
      specialization: 'SEO & Social Media',
      experience: '8+ years',
      image: 'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=300'
    }
  ];

  const specializations = [
    { name: 'Web Development', icon: Globe, description: 'Learn to build modern websites and applications.' },
    { name: 'Data Science & AI', icon: Lightbulb, description: 'Analyze data and create intelligent AI-driven solutions.' },
    { name: 'Digital Marketing', icon: TrendingUp, description: 'Grow businesses through SEO, social media, and ads.' },
    { name: 'Cloud Computing', icon: Shield, description: 'Master AWS, Azure, and DevOps tools for cloud solutions.' },
    { name: 'Cybersecurity', icon: Settings, description: 'Protect systems, networks, and data with security tools.' },
    { name: 'UI/UX Design', icon: Star, description: 'Design engaging and user-friendly digital experiences.' }
  ];

  const facilities = [
    {
      title: 'Virtual Labs',
      description: 'Hands-on coding practice in browser-based labs.',
      icon: <BookOpen className="civil-facility-icon" />
    },
    {
      title: 'Live Classes',
      description: 'Interactive live sessions with industry experts.',
      icon: <Users className="civil-facility-icon" />
    },
    {
      title: '24/7 LMS Access',
      description: 'Learn anytime, anywhere with our online learning platform.',
      icon: <Globe className="civil-facility-icon" />
    },
    {
      title: 'Career Support',
      description: 'Resume building, interview prep, and placement guidance.',
      icon: <Briefcase className="civil-facility-icon" />
    }
  ];

  const careerStats = [
    { icon: <Users />, value: '96%', label: 'Placement Rate' },
    { icon: <Award />, value: '₹12 LPA', label: 'Top Package' },
    { icon: <Briefcase />, value: '200+', label: 'Hiring Partners' },
    { icon: <Star />, value: '4.8/5', label: 'Student Rating' }
  ];

  const testimonials = [
    {
      name: 'Riya Kapoor',
      role: 'Software Developer, Infosys',
      year: '2023 Graduate',
      content: 'Profenaa’s Full-Stack course helped me land a developer job with hands-on coding practice and real projects.',
      image: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'Amit Verma',
      role: 'Data Analyst, Deloitte',
      year: '2022 Graduate',
      content: 'The Data Science program was a game changer. The career support team guided me through interviews and placements.',
      image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  return (
    <>
      <Navbar /><br /><br />
      <div className="civil-page">
        
        {/* Hero Section */}
        <section className="civil-hero">
          <div className="civil-hero-overlay"></div>
          <div className="civil-hero-content">
            <h1 className="civil-hero-title">
              Profenaa Online Learning
              <span className="civil-hero-subtitle">Learn. Upgrade. Succeed.</span>
            </h1>
            <p className="civil-hero-description">
              World-class online courses designed to help you build in-demand skills 
              and secure top career opportunities. Learn from experts anytime, anywhere.
            </p>
            <div className="civil-hero-actions">
              <button className="civil-btn civil-btn-primary">Start Learning</button>
              <button className="civil-btn civil-btn-secondary">Download Brochure</button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="civil-stats">
          <div className="civil-container">
            <div className="civil-stats-grid">
              {careerStats.map((stat, index) => (
                <div key={index} className="civil-stat-card">
                  <div className="civil-stat-icon">{stat.icon}</div>
                  <div className="civil-stat-value">{stat.value}</div>
                  <div className="civil-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Programs Section */}
        <section className="civil-programs">
          <div className="civil-container">
            <div className="civil-section-header">
              <h2 className="civil-section-title">Popular Programs</h2>
              <p className="civil-section-subtitle">
                Choose from industry-ready programs designed to boost your career growth
              </p>
            </div>
            <div className="civil-programs-grid">
              {programs.map((program, index) => (
                <div key={index} className="civil-program-card">
                  <div className="civil-program-header">
                    <h3 className="civil-program-title">{program.title}</h3>
                    <span className="civil-program-duration">{program.duration}</span>
                  </div>
                  <p className="civil-program-description">{program.description}</p>
                  <ul className="civil-program-features">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="civil-program-feature">
                        <span className="civil-feature-bullet"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="civil-program-btn">Explore Course</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Faculty Section */}
        <section className="civil-faculty">
          <div className="civil-container">
            <div className="civil-section-header">
              <h2 className="civil-section-title">Expert Instructors</h2>
              <p className="civil-section-subtitle">
                Learn directly from professionals with years of teaching and industry experience
              </p>
            </div>
            <div className="civil-faculty-grid">
              {faculty.map((member, index) => (
                <div key={index} className="civil-faculty-card">
                  <div className="civil-faculty-image">
                    <img src={member.image} alt={member.name} />
                    <div className="civil-faculty-overlay">
                      <div className="civil-faculty-experience">{member.experience}</div>
                    </div>
                  </div>
                  <div className="civil-faculty-info">
                    <h3 className="civil-faculty-name">{member.name}</h3>
                    <p className="civil-faculty-position">{member.position}</p>
                    <p className="civil-faculty-specialization">{member.specialization}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Specializations Section */}
        <section className="civil-specializations">
          <div className="civil-container">
            <div className="civil-section-header">
              <h2 className="civil-section-title">Specialized Learning Tracks</h2>
              <p className="civil-section-subtitle">
                Explore career-focused specializations designed for today’s job market
              </p>
            </div>
            <div className="civil-specializations-grid">
              {specializations.map((spec, index) => {
                const IconComponent = spec.icon;
                return (
                  <div key={index} className="civil-specialization-card">
                    <div className="civil-specialization-icon">
                      <IconComponent className="civil-spec-icon" />
                    </div>
                    <h3 className="civil-specialization-title">{spec.name}</h3>
                    <p className="civil-specialization-description">{spec.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Facilities Section */}
        <section className="civil-facilities">
          <div className="civil-container">
            <div className="civil-section-header">
              <h2 className="civil-section-title">Learning Support</h2>
              <p className="civil-section-subtitle">
                Tools and resources designed to give you a complete learning experience
              </p>
            </div>
            <div className="civil-facilities-grid">
              {facilities.map((facility, index) => (
                <div key={index} className="civil-facility-card">
                  <div className="civil-facility-icon-container">
                    {facility.icon}
                  </div>
                  <h3 className="civil-facility-title">{facility.title}</h3>
                  <p className="civil-facility-description">{facility.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="civil-testimonials">
          <div className="civil-container">
            <div className="civil-section-header">
              <h2 className="civil-section-title">Learner Success Stories</h2>
              <p className="civil-section-subtitle">
                Hear from our students who built successful careers with Profenaa
              </p>
            </div>
            <div className="civil-testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="civil-testimonial-card">
                  <div className="civil-testimonial-content">
                    <p className="civil-testimonial-text">"{testimonial.content}"</p>
                  </div>
                  <div className="civil-testimonial-author">
                    <img src={testimonial.image} alt={testimonial.name} className="civil-testimonial-avatar" />
                    <div className="civil-testimonial-info">
                      <h4 className="civil-testimonial-name">{testimonial.name}</h4>
                      <p className="civil-testimonial-role">{testimonial.role}</p>
                      <p className="civil-testimonial-year">{testimonial.year}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="civil-cta">
          <div className="civil-container">
            <div className="civil-cta-content">
              <h2 className="civil-cta-title">Start Your Online Learning Journey</h2>
              <p className="civil-cta-description">
                Join Profenaa Online Learning and gain skills that employers value. 
                Flexible schedules, expert mentors, and career support.
              </p>
              <div className="civil-cta-actions">
                <button className="civil-btn civil-btn-primary civil-btn-large">Enroll Now</button>
                <button className="civil-btn civil-btn-outline civil-btn-large">Get Free Demo</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default OnlineLearning;
